import { errorMessage } from '@/lib/errorMessage';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@/lib/router-compat';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ArrowRight, Mail, MapPin, CheckCircle2, Loader2, Check } from 'lucide-react';
import { Github, Linkedin } from '@/components/icons/brand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

const LocationMap = lazy(() =>
  import('@/components/LocationMap').then((module) => ({ default: module.LocationMap })),
);

const INQUIRY_TYPES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'sales', label: 'Sales' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'careers', label: 'Careers' },
] as const;

type InquiryType = (typeof INQUIRY_TYPES)[number]['value'];

const contactSchema = z.object({
  inquiryType: z.enum(['general', 'sales', 'partnership', 'careers'], {
    error: 'Please select an inquiry type',
  }),
  firstName: z.string().trim().min(1, 'Please enter your first name').max(100, 'First name is too long'),
  lastName: z.string().trim().min(1, 'Please enter your last name').max(100, 'Last name is too long'),
  email: z.string().trim().min(1, 'Please enter your email').email('Please enter a valid email address').max(255, 'Email is too long'),
  phone: z.string().trim().max(50, 'Phone number is too long').optional().or(z.literal('')),
  company: z.string().trim().max(200, 'Company name is too long').optional().or(z.literal('')),
  message: z.string().trim().min(10, 'Please share a bit more so we can help (at least 10 characters)').max(5000, 'Message must be under 5,000 characters'),
  website: z.string().max(200).optional().or(z.literal('')), // honeypot
});

type ContactFormValues = z.infer<typeof contactSchema>;

interface UtmParams {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
}

function captureUtm(): UtmParams {
  if (typeof window === 'undefined') {
    return { source: null, medium: null, campaign: null, term: null, content: null };
  }
  const params = new URLSearchParams(window.location.search);
  const pick = (k: string) => params.get(k)?.trim().slice(0, 200) || null;
  return {
    source: pick('utm_source'),
    medium: pick('utm_medium'),
    campaign: pick('utm_campaign'),
    term: pick('utm_term'),
    content: pick('utm_content'),
  };
}

function captureInitialInquiryType(): InquiryType {
  if (typeof window === 'undefined') return 'general';
  const raw = new URLSearchParams(window.location.search).get('inquiry');
  const valid = INQUIRY_TYPES.map((t) => t.value) as readonly string[];
  return (raw && valid.includes(raw) ? raw : 'general') as InquiryType;
}

// Format US phone numbers as the user types: (555) 000-0000
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Smart placeholder per inquiry type to nudge better lead quality
const MESSAGE_PLACEHOLDERS: Record<InquiryType, string> = {
  general: 'Your message',
  sales: 'Tell us about your project, timeline, and team size.',
  partnership: 'Tell us about your organization and the partnership you have in mind.',
  careers: 'Share the role or area you are interested in, plus a link to your resume or LinkedIn.',
};

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const DRAFT_STORAGE_KEY = 'akiva-contact-draft';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Turnstile is mounted lazily on first form interaction. Mounting it on page
  // load pulled in the challenges.cloudflare.com iframe for every homepage
  // visitor (Contact renders on "/"), which logged a `__cf_bm` cross-domain
  // cookie rejection and loaded a third-party script nobody needed.
  const [formEngaged, setFormEngaged] = useState(false);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const formLoadedAtRef = useRef<number>(Date.now());
  const utmRef = useRef<UtmParams>({ source: null, medium: null, campaign: null, term: null, content: null });
  const referrerRef = useRef<string | null>(null);

  useEffect(() => {
    formLoadedAtRef.current = Date.now();
    utmRef.current = captureUtm();
    referrerRef.current = typeof document !== 'undefined' && document.referrer ? document.referrer.slice(0, 2048) : null;
  }, []);

  useEffect(() => {
    const element = mapContainerRef.current;
    if (!element || shouldLoadMap) return;
    if (!('IntersectionObserver' in window)) {
      setShouldLoadMap(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadMap(true);
          observer.disconnect();
        }
      },
      // Prepare the interactive map before the visitor reaches it, not on arrival.
      { rootMargin: '1200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldLoadMap]);

  const initialInquiryType = useMemo(() => captureInitialInquiryType(), []);

  const initialDraft = useMemo<Partial<ContactFormValues>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.sessionStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Partial<ContactFormValues>;
      // Drop honeypot from rehydration
      delete (parsed as { website?: string }).website;
      return parsed;
    } catch {
      return {};
    }
  }, []);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    mode: 'onTouched',
    defaultValues: {
      inquiryType: (initialDraft.inquiryType as InquiryType) || initialInquiryType,
      firstName: initialDraft.firstName ?? '',
      lastName: initialDraft.lastName ?? '',
      email: initialDraft.email ?? '',
      phone: initialDraft.phone ?? '',
      company: initialDraft.company ?? '',
      message: initialDraft.message ?? '',
      website: '',
    },
  });

  // Persist draft to sessionStorage (debounced) so accidental refresh doesn't lose work
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const subscription = form.watch((values) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          const { website: _w, ...rest } = values;
          window.sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(rest));
        } catch {
          // ignore quota/serialize errors
        }
      }, 500);
    });
    return () => {
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [form]);

  // Watch form validity to enable/disable submit
  const isValid = form.formState.isValid;

  const onSubmit = async (values: ContactFormValues) => {
    if (!turnstileToken) {
      toast.error('Please complete the security check');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('send-contact-form', {
        body: {
          ...values,
          phone: values.phone || null,
          company: values.company || null,
          turnstileToken,
          formLoadedAt: formLoadedAtRef.current,
          utm: utmRef.current,
          referrer: referrerRef.current,
        },
      });

      if (error) throw error;

      setIsSubmitted(true);
      form.reset({
        inquiryType: initialInquiryType,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        message: '',
          website: '',
      });
      try {
        window.sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch {
        // ignore
      }
      toast.success('Message sent', {
        description: "Thanks, we'll get back to you as soon as possible.",
      });
    } catch (error: unknown) {
      logger.error('Error sending contact form:', error);
      const message = errorMessage(error, 'Please try again later.');
      toast.error('Failed to send message', { description: message });
    } finally {
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      setIsSubmitting(false);
    }
  };

  // Focus the first invalid field on validation failure
  const onInvalid = (errors: Record<string, unknown>) => {
    const order: (keyof ContactFormValues)[] = ['inquiryType', 'firstName', 'lastName', 'email', 'phone', 'company', 'message'];
    const firstErr = order.find((name) => name in errors);
    if (firstErr) {
      form.setFocus(firstErr);
    }
  };

  return (
    <section id="contact" tabIndex={-1} className="scroll-mt-20 section-padding bg-muted/30 text-foreground">
      <div className="container-max">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center rounded-full border border-primary/30 px-3 py-1 text-xs font-medium tracking-wide uppercase bg-primary/10 text-primary mb-5">
            Contact
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-foreground">
            Contact Us
          </h2>
          <p className="text-lg text-muted-foreground">
            Ready to discuss how we can work with your organization? Reach out to start the conversation.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <Card className="rounded-2xl border border-primary/35 bg-card backdrop-blur-sm focus-within:border-primary/50 transition-all duration-300 overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 p-8 lg:p-10 flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-semibold tracking-tight mb-4 text-foreground">Ready to get started?</h3>
                    <p className="text-muted-foreground mb-10 leading-relaxed">
                      Whether it's engineering, products, or strategy — start here, and we'll get back to you as soon as possible.
                    </p>

                    <div className="space-y-5">
                      <div className="flex items-start gap-4">
                        <div className="p-2.5 rounded-xl bg-primary/10 text-primary mt-0.5">
                          <Mail className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">Email</p>
                          <a href="mailto:info@akiva.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                            info@akiva.com
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="p-2.5 rounded-xl bg-primary/10 text-primary mt-0.5">
                          <MapPin className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">Location</p>
                          <p className="text-sm text-muted-foreground">
                            Leesburg, Virginia, United States
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12 pt-8 space-y-8">
                    <div ref={mapContainerRef}>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">Find us</p>
                      {shouldLoadMap ? (
                        <Suspense fallback={<div className="h-56 rounded-lg bg-muted/40" aria-label="Loading map" />}>
                          <LocationMap
                            center={[-77.5636, 39.1157]}
                            zoom={13}
                            heightClass="h-56"
                            label="AKIVA HQ — Leesburg, VA"
                          />
                        </Suspense>
                      ) : (
                        <div className="h-56 rounded-lg bg-muted/40" role="img" aria-label="Map loads when scrolled into view" />
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">Connect</p>
                      <div className="flex gap-2">
                        <a href="https://www.linkedin.com/company/akiva-ai" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg border border-border hover:border-foreground/40 hover:text-foreground transition-colors duration-150 text-muted-foreground" aria-label="LinkedIn">
                          <Linkedin className="h-4 w-4" />
                        </a>
                        <a href="https://github.com/akiva-ai" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg border border-border hover:border-foreground/40 hover:text-foreground transition-colors duration-150 text-muted-foreground" aria-label="Github">
                          <Github className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3 p-8 lg:p-10">
                  {isSubmitted ? (
                    <div role="status" aria-live="polite" className="h-full flex flex-col items-center justify-center text-center space-y-4 py-8">
                      <div className="p-4 rounded-full bg-success/10 text-success-text">
                        <CheckCircle2 className="h-10 w-10" />
                      </div>
                      <h3 className="text-2xl font-bold">Thanks for reaching out.</h3>
                      <p className="text-muted-foreground max-w-md">
                        We received your message and will get back to you as soon as possible. A confirmation has been sent to your email.
                      </p>
                      <Button variant="outline" onClick={() => setIsSubmitted(false)} className="mt-2 focus-visible:ring-1 focus-visible:ring-ring/50 focus-visible:ring-offset-0">
                        Send another message
                      </Button>
                    </div>
                  ) : (
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} onFocusCapture={() => setFormEngaged(true)} className="space-y-6" noValidate>
                        <p className="text-xs text-muted-foreground">
                          Fields marked <span aria-hidden="true">*</span> are required.
                        </p>
                        <FormField
                          control={form.control}
                          name="inquiryType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground" required>Inquiry type</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger aria-label="Inquiry type" className="bg-card/50 border-border text-foreground hover:bg-card/60 transition-colors focus:outline-none focus:ring-1 focus:ring-ring/50 focus:ring-offset-0 data-[state=open]:bg-card/60 data-[placeholder]:text-muted-foreground">
                                    <SelectValue placeholder="Select an inquiry type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="bg-card text-card-foreground border-border shadow-none backdrop-blur-none">
                                  {INQUIRY_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value} className="focus:bg-accent focus:text-accent-foreground">{t.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-muted-foreground font-normal" />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-foreground" required>First name</FormLabel>
                                <FormControl>
                                    <Input
                                      {...field}
                                      placeholder="Your first name"
                                      autoComplete="given-name"
                                      maxLength={100}
                                      className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-ring-subtle"
                                    />
                                </FormControl>
                                <FormMessage className="text-muted-foreground font-normal" />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-foreground" required>Last name</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder="Your last name"
                                    autoComplete="family-name"
                                    maxLength={100}
                                    className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-ring-subtle"
                                  />
                                </FormControl>
                                <FormMessage className="text-muted-foreground font-normal" />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field, fieldState }) => {
                              const isValid = !fieldState.invalid && field.value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value);
                              return (
                                <FormItem>
                                  <FormLabel className="text-foreground" required>Email</FormLabel>
                                  <div className="relative">
                                    <FormControl>
                                      <Input
                                        {...field}
                                        type="email"
                                        placeholder="you@company.com"
                                        autoComplete="email"
                                        spellCheck={false}
                                        maxLength={255}
                                        className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-ring-subtle pr-9"
                                      />
                                    </FormControl>
                                    {isValid && (
                                      <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-success-text" aria-hidden="true" />
                                    )}
                                  </div>
                                  <FormMessage className="text-muted-foreground font-normal" />
                                </FormItem>
                              );
                            }}
                          />
                          <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-foreground">Phone <span className="text-muted-foreground text-xs">(optional)</span></FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    type="tel"
                                    placeholder="(555) 000-0000"
                                    autoComplete="tel"
                                    maxLength={20}
                                    onChange={(e) => field.onChange(formatPhone(e.target.value))}
                                    className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-ring-subtle"
                                  />
                                </FormControl>
                                <FormMessage className="text-muted-foreground font-normal" />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="company"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground">Company <span className="text-muted-foreground text-xs">(optional)</span></FormLabel>
                              <FormControl>
                                  <Input
                                    {...field}
                                    placeholder="Your organization"
                                    autoComplete="organization"
                                    maxLength={200}
                                    className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-ring-subtle"
                                  />
                              </FormControl>
                              <FormMessage className="text-muted-foreground font-normal" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="message"
                          render={({ field }) => {
                            const currentInquiry = form.watch('inquiryType');
                            const charCount = field.value?.length ?? 0;
                            return (
                              <FormItem>
                                <div className="flex items-baseline justify-between">
                                  <FormLabel className="text-foreground" required>Message</FormLabel>
                                  <span className={`text-xs tabular-nums ${charCount > 4500 ? 'text-warning-text' : 'text-muted-foreground'}`}>
                                    {charCount.toLocaleString()} / 5,000
                                  </span>
                                </div>
                                <FormControl>
                                  <Textarea
                                    {...field}
                                    placeholder={MESSAGE_PLACEHOLDERS[currentInquiry] ?? 'Your message'}
                                    maxLength={5000}
                                    className="min-h-[150px] resize-none bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-ring-subtle"
                                  />
                                </FormControl>
                                <FormMessage className="text-muted-foreground font-normal" />
                              </FormItem>
                            );
                          }}
                        />

                        {/* Honeypot: hidden from humans via tabIndex + aria + positioning */}
                        <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                          <label>
                            Website (leave blank)
                            <input
                              type="text"
                              tabIndex={-1}
                              autoComplete="off"
                              {...form.register('website')}
                            />
                          </label>
                        </div>

                        {TURNSTILE_SITE_KEY ? (
                          formEngaged && (
                            <div className="flex justify-center">
                            <Turnstile
                              ref={turnstileRef}
                              siteKey={TURNSTILE_SITE_KEY}
                              options={{ action: 'contact', theme: 'auto', size: 'normal', appearance: 'interaction-only' }}
                              onSuccess={setTurnstileToken}
                              onError={() => setTurnstileToken(null)}
                              onExpire={() => setTurnstileToken(null)}
                            />
                            </div>
                          )
                        ) : (
                          <p className="text-xs text-destructive-text">
                            Security check is not configured. Set VITE_TURNSTILE_SITE_KEY in your environment.
                          </p>
                        )}


                        <Button
                          type="submit"
                          disabled={isSubmitting || (form.formState.isSubmitted && !isValid)}
                          className="w-full h-12 text-base group bg-primary text-primary-foreground hover:bg-primary/90 border-none rounded-lg transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:ring-1 focus-visible:ring-ring/50 focus-visible:ring-offset-0"
                          size="lg"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending your message...
                            </>
                          ) : (
                            <>
                              Send Message
                              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-center text-muted-foreground">
                          By submitting this form, you agree to our{' '}
                          <Link to="/privacy-policy" className="underline hover:text-foreground transition-colors">
                            Privacy Policy
                          </Link>
                          .
                        </p>
                      </form>
                    </Form>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
